import { useCallback, useEffect, useState } from "react";
import { Bell } from "lucide-react";
import { Button } from "@/components/ui/button";

// Replaces the hard push gate on /seller. Guideline 4.5.4 forbids requiring
// push to use the app, so a seller can now skip the permission screen. This
// keeps asking instead: it reappears on every dashboard visit while push is
// off, which reaches people a one-time wall never would (iOS shows the system
// prompt once, so anyone who tapped "Don't Allow" is permanently stuck at a
// wall but can still be recovered by a banner pointing them at Settings).
export function PushOffBanner() {
  const [state, setState] = useState<"unknown" | "granted" | "denied" | "prompt">("unknown");
  const [requesting, setRequesting] = useState(false);

  const refresh = useCallback(async () => {
    try {
      const { Capacitor } = await import("@capacitor/core");
      if (!Capacitor.isNativePlatform()) return setState("granted");
      const { FirebaseMessaging } = await import("@capacitor-firebase/messaging");
      const result = await FirebaseMessaging.checkPermissions();
      const receive = String(result.receive || "");
      setState(receive === "granted" ? "granted" : receive === "denied" ? "denied" : "prompt");
    } catch {
      setState("granted");
    }
  }, []);

  useEffect(() => {
    void refresh();
  }, [refresh]);

  if (state === "granted" || state === "unknown") return null;

  const enable = async () => {
    setRequesting(true);
    try {
      const { FirebaseMessaging } = await import("@capacitor-firebase/messaging");
      const result = await FirebaseMessaging.requestPermissions();
      if (result.receive === "granted") {
        await FirebaseMessaging.getToken().catch(() => undefined);
      }
      await refresh();
    } catch {
      /* leave the banner up so they can try again */
    } finally {
      setRequesting(false);
    }
  };

  return (
    <div className="mb-4 rounded-lg border border-amber-300 bg-amber-50 p-3 text-sm dark:border-amber-800/40 dark:bg-amber-950/30">
      <div className="flex items-start gap-2">
        <Bell className="mt-0.5 h-4 w-4 shrink-0 text-amber-700 dark:text-amber-400" />
        <div className="flex-1">
          <p className="font-semibold text-foreground">Push notifications are off</p>
          <p className="mt-0.5 text-muted-foreground">
            You'll still get an email for every order, but push tells you the moment one lands.
          </p>
          {state === "denied" ? (
            <p className="mt-2 text-xs text-muted-foreground">
              Turn them on in Settings, then Apps, then Stoop, then Notifications.
            </p>
          ) : (
            <Button size="sm" className="mt-2" onClick={enable} disabled={requesting}>
              {requesting ? "Asking…" : "Turn on notifications"}
            </Button>
          )}
        </div>
      </div>
    </div>
  );
}
